import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { Link } from 'react-router';

import { BreakableEmail } from '@/components/BreakableEmail';
import { Button } from '@/components/Button/Button';

import { formatGradeValue } from './gradebookTypes';
import type { GradebookAssignment, GradebookStudent } from './gradebookTypes';
import styles from './GradebookGrid.module.css';

interface GradebookGridProps {
  assignments: GradebookAssignment[];
  // When given, an assignment with questions links its title to the
  // assignment's page, where the class's questions can be managed.
  courseId?: string;
  loadingProgress: { loaded: number; total: number } | null;
  onSelectStudent: (studentId: string) => void;
  onSort: (column: string) => void;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  students: GradebookStudent[];
}

function getScoreBand(score: number | null | undefined, possible: number) {
  if (score === null || score === undefined || possible <= 0) {
    return 'empty';
  }

  const percent = score / possible;

  if (percent >= 0.8) {
    return 'strong';
  }

  if (percent >= 0.5) {
    return 'mid';
  }

  return 'low';
}

type ScoreKind = 'homework' | 'participation' | 'preLecture';

// Display columns in left-to-right order, with the per-assignment flag and the
// short subheader label for each.
const SCORE_KINDS: ReadonlyArray<{ kind: ScoreKind; label: string }> = [
  { kind: 'preLecture', label: 'PL' },
  { kind: 'homework', label: 'HW' },
  { kind: 'participation', label: 'Coach' },
];

function assignmentHasKind(assignment: GradebookAssignment, kind: ScoreKind) {
  if (kind === 'preLecture') {
    return assignment.hasPreLecture;
  }
  if (kind === 'participation') {
    return assignment.hasParticipation;
  }
  return assignment.hasHomework;
}

function getScoreValue(
  student: GradebookStudent,
  assignment: GradebookAssignment,
  kind: ScoreKind,
) {
  const score = student.assignmentScores[assignment.componentUid];

  if (!score) {
    return null;
  }

  if (kind === 'preLecture') {
    return score.preLectureScore;
  }
  if (kind === 'participation') {
    return score.participationScore;
  }
  return score.homeworkScore;
}

function getPossibleValue(assignment: GradebookAssignment, kind: ScoreKind) {
  if (kind === 'preLecture') {
    return assignment.preLectureMaxScore;
  }
  if (kind === 'participation') {
    return assignment.participationMaxScore;
  }
  return assignment.homeworkMaxScore;
}

function formatEarnedScoreCell(score: number | null | undefined) {
  // Student rows show only the raw earned score. An unanswered assignment shows
  // 0 (not a dash) so the view matches the spreadsheet export.
  if (score === null || score === undefined) {
    return '0';
  }

  return formatGradeValue(score);
}

export function GradebookGrid({
  assignments,
  courseId,
  loadingProgress,
  onSelectStudent,
  onSort,
  sortColumn,
  sortDirection,
  students,
}: GradebookGridProps) {
  const isLoadingAnyGrades = loadingProgress
    ? loadingProgress.loaded < loadingProgress.total
    : false;

  const preLectureTotalMax = assignments.reduce(
    (total, assignment) => total + assignment.preLectureMaxScore,
    0,
  );
  const homeworkTotalMax = assignments.reduce(
    (total, assignment) => total + assignment.homeworkMaxScore,
    0,
  );
  const participationTotalMax = assignments.reduce(
    (total, assignment) => total + assignment.participationMaxScore,
    0,
  );
  const hasAnyParticipation = participationTotalMax > 0;

  // The header rows wrap to variable heights, so measure them and feed the
  // cumulative offsets to CSS. This lets the title row, the PL/HW subheader row,
  // and the Max Score row each stay pinned at the correct vertical position
  // while the student rows scroll beneath them.
  const titleRowRef = useRef<HTMLTableRowElement>(null);
  const subheaderRowRef = useRef<HTMLTableRowElement>(null);
  const [headerRowHeights, setHeaderRowHeights] = useState({
    subheader: 0,
    title: 0,
  });

  useLayoutEffect(() => {
    const measure = () => {
      setHeaderRowHeights({
        subheader: subheaderRowRef.current?.offsetHeight ?? 0,
        title: titleRowRef.current?.offsetHeight ?? 0,
      });
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(measure);

    if (titleRowRef.current) {
      observer.observe(titleRowRef.current);
    }

    if (subheaderRowRef.current) {
      observer.observe(subheaderRowRef.current);
    }

    return () => observer.disconnect();
  }, [assignments]);

  const stickyOffsetStyle = {
    '--gb-header-row1-height': `${headerRowHeights.title}px`,
    '--gb-header-row2-height': `${headerRowHeights.subheader}px`,
  } as CSSProperties;

  return (
    <div className={styles.surface}>
      <div className={styles.scrollRegion}>
        <table className={styles.table} style={stickyOffsetStyle}>
          <caption className={styles.caption}>
            Gradebook roster and assignment scores
          </caption>
          <thead>
            <tr className={styles.headerRowTitles} ref={titleRowRef}>
              <th className={styles.stickyColPrimary} rowSpan={2} scope="col">
                <button
                  className={styles.sortButton}
                  onClick={() => onSort('lastName')}
                  type="button"
                >
                  Last Name
                  {sortColumn === 'lastName' ? (
                    <span aria-hidden="true">{sortDirection === 'asc' ? ' ↓' : ' ↑'}</span>
                  ) : null}
                </button>
              </th>
              <th className={styles.stickyColSecondary} rowSpan={2} scope="col">
                <button
                  className={styles.sortButton}
                  onClick={() => onSort('firstName')}
                  type="button"
                >
                  First Name
                  {sortColumn === 'firstName' ? (
                    <span aria-hidden="true">{sortDirection === 'asc' ? ' ↓' : ' ↑'}</span>
                  ) : null}
                </button>
              </th>
              <th rowSpan={2} scope="col">
                <button
                  className={styles.sortButton}
                  onClick={() => onSort('email')}
                  type="button"
                >
                  Email
                  {sortColumn === 'email' ? (
                    <span aria-hidden="true">{sortDirection === 'asc' ? ' ↓' : ' ↑'}</span>
                  ) : null}
                </button>
              </th>
              <th rowSpan={2} scope="col">
                <button
                  className={styles.sortButton}
                  onClick={() => onSort('instituteId')}
                  type="button"
                >
                  Student ID
                  {sortColumn === 'instituteId' ? (
                    <span aria-hidden="true">{sortDirection === 'asc' ? ' ↓' : ' ↑'}</span>
                  ) : null}
                </button>
              </th>
              {assignments.map((assignment) => (
                <th
                  className={styles.assignmentHeader}
                  colSpan={
                    Number(assignment.hasPreLecture) +
                    Number(assignment.hasHomework) +
                    Number(assignment.hasParticipation)
                  }
                  key={assignment.componentUid}
                  scope="colgroup"
                  title={assignment.title}
                >
                  {courseId && (assignment.hasHomework || assignment.hasPreLecture) ? (
                    <Link
                      className={styles.assignmentLink}
                      to={`/app/courses/${courseId}/grades/assignments/${assignment.componentUid}`}
                    >
                      {assignment.title}
                    </Link>
                  ) : (
                    assignment.title
                  )}
                </th>
              ))}
              <th rowSpan={2} scope="col">
                <button
                  className={styles.sortButton}
                  onClick={() => onSort('totalPreLecture')}
                  type="button"
                >
                  PL Total
                  {sortColumn === 'totalPreLecture' ? (
                    <span aria-hidden="true">{sortDirection === 'asc' ? ' ↓' : ' ↑'}</span>
                  ) : null}
                </button>
              </th>
              <th rowSpan={2} scope="col">
                <button
                  className={styles.sortButton}
                  onClick={() => onSort('totalHomework')}
                  type="button"
                >
                  HW Total
                  {sortColumn === 'totalHomework' ? (
                    <span aria-hidden="true">{sortDirection === 'asc' ? ' ↓' : ' ↑'}</span>
                  ) : null}
                </button>
              </th>
              {hasAnyParticipation ? (
                <th rowSpan={2} scope="col">
                  <button
                    className={styles.sortButton}
                    onClick={() => onSort('totalParticipation')}
                    type="button"
                  >
                    Coach Total
                    {sortColumn === 'totalParticipation' ? (
                      <span aria-hidden="true">{sortDirection === 'asc' ? ' ↓' : ' ↑'}</span>
                    ) : null}
                  </button>
                </th>
              ) : null}
            </tr>
            <tr className={styles.headerRowSub} ref={subheaderRowRef}>
              {assignments.flatMap((assignment) =>
                SCORE_KINDS.filter(({ kind }) => assignmentHasKind(assignment, kind)).map(
                  ({ kind, label }) => (
                    <th
                      aria-sort={
                        sortColumn === `${kind}:${assignment.componentUid}`
                          ? sortDirection === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                      key={`${assignment.componentUid}-${kind}`}
                      scope="col"
                    >
                      <button
                        className={styles.sortButton}
                        onClick={() => onSort(`${kind}:${assignment.componentUid}`)}
                        type="button"
                      >
                        {label}
                      </button>
                    </th>
                  ),
                ),
              )}
            </tr>
            <tr className={styles.maxScoreRow}>
              <th className={styles.stickyColPrimary} scope="row">
                Max Score
              </th>
              <td className={styles.stickyColSecondary} />
              <td />
              <td />
              {assignments.flatMap((assignment) =>
                SCORE_KINDS.filter(({ kind }) => assignmentHasKind(assignment, kind)).map(
                  ({ kind }) => (
                    <td key={`max-${assignment.componentUid}-${kind}`}>
                      {formatGradeValue(getPossibleValue(assignment, kind))}
                    </td>
                  ),
                ),
              )}
              <td>{formatGradeValue(preLectureTotalMax)}</td>
              <td>{formatGradeValue(homeworkTotalMax)}</td>
              {hasAnyParticipation ? (
                <td>{formatGradeValue(participationTotalMax)}</td>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {students.map((student) => (
              <tr key={student.id}>
                <th className={styles.stickyColPrimary} scope="row">
                  <Button
                    className={styles.studentLink}
                    fullWidth
                    onClick={() => onSelectStudent(student.id)}
                    variant="ghost"
                  >
                    {student.lastName || '—'}
                  </Button>
                </th>
                <td className={styles.stickyColSecondary}>
                  <Button
                    className={styles.studentLink}
                    fullWidth
                    onClick={() => onSelectStudent(student.id)}
                    variant="ghost"
                  >
                    {student.firstName || '—'}
                  </Button>
                </td>
                <td className={styles.emailCell}>
                  {student.email ? <BreakableEmail address={student.email} /> : '—'}
                </td>
                <td>{student.instituteId || '—'}</td>
                {assignments.flatMap((assignment) =>
                  SCORE_KINDS.filter(({ kind }) =>
                    assignmentHasKind(assignment, kind),
                  ).map(({ kind }) => {
                    const score = getScoreValue(student, assignment, kind);
                    const possible = getPossibleValue(assignment, kind);

                    return (
                      <td
                        data-band={getScoreBand(score, possible)}
                        key={`${student.id}-${assignment.componentUid}-${kind}`}
                      >
                        {!student.hasLoadedGrades && isLoadingAnyGrades ? (
                          <span className={styles.loadingCell}>Loading…</span>
                        ) : (
                          formatEarnedScoreCell(score)
                        )}
                      </td>
                    );
                  }),
                )}
                <td data-band={getScoreBand(student.totalPreLectureScore, student.totalPreLecturePossible)}>
                  {!student.hasLoadedGrades && isLoadingAnyGrades
                    ? <span className={styles.loadingCell}>Loading…</span>
                    : formatEarnedScoreCell(student.totalPreLectureScore)}
                </td>
                <td data-band={getScoreBand(student.totalHomeworkScore, student.totalHomeworkPossible)}>
                  {!student.hasLoadedGrades && isLoadingAnyGrades
                    ? <span className={styles.loadingCell}>Loading…</span>
                    : formatEarnedScoreCell(student.totalHomeworkScore)}
                </td>
                {hasAnyParticipation ? (
                  <td data-band={getScoreBand(student.totalParticipationScore, student.totalParticipationPossible)}>
                    {!student.hasLoadedGrades && isLoadingAnyGrades
                      ? <span className={styles.loadingCell}>Loading…</span>
                      : formatEarnedScoreCell(student.totalParticipationScore)}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
